import { adminPath, useScope } from "../lib/scope";
import { CpuIcon } from "@primer/octicons-react";
import { useState } from "react";
import { useAction, useRunners } from "../api/hooks";
import type { RunnerPlatform, RunnersView } from "../api/types";
import { ErrorMessage, Query } from "../components/common";
import { Subhead } from "../components/SettingsLayout";
import { Card, CardContent, CardHeader } from "../components/ui/card";
import { CheckboxField } from "../components/ui/checkbox";
import { ConfirmDialog } from "../components/ui/confirm-dialog";
import { cn } from "../lib/utils";
import { isLive, NewRunnerDialog, RegisteredRunners } from "./AdminRunnersRegistered";
import { Deployment, platformLabel } from "./RunnerDeploy";

export function AdminRunners() {
  const q = useRunners();
  return (
    <>
      <Subhead title="Runners">
        Runners pick up CI jobs. Each one has its own token, so you can pause it, limit it to some repositories, rotate its token or revoke it. New runner walks
        you through starting it on a machine or deploying it to a cluster.
      </Subhead>
      <Query q={q}>{(v) => <RunnersPage v={v} />}</Query>
    </>
  );
}

function RunnersPage({ v }: { v: RunnersView }) {
  const org = useScope();
  const [creating, setCreating] = useState(false);
  const live = v.runners.filter(isLive);
  const liveOn = (p: RunnerPlatform) => live.filter((r) => r.platform === p).length;
  const operator = !org ? v.placements.find((s) => s.placement.managed && s.setup.steps.length > 0) : undefined;
  return (
    <>
      <RegisteredRunners
        registered={v.registered}
        instances={v.runners}
        placements={v.placements.map((s) => s.placement)}
        strict={v.strict}
        onNew={() => setCreating(true)}
        footer={v.placements.length > 0 && <Enforce v={v} liveOn={liveOn} />}
      />
      {v.placements.some((s) => s.placement.managed) && <ManagedUsage usage={v.usage} site={!org} />}
      {operator && (
        <details className="mt-4">
          <summary className="cursor-pointer text-xs text-muted-foreground">
            Operator setup for the managed {platformLabel(operator.placement.platform)} pool
          </summary>
          <Deployment platform={operator.placement.platform} />
        </details>
      )}
      <NewRunnerDialog open={creating} onOpenChange={setCreating} />
    </>
  );
}

/** What the managed pool ran this month: the organization's own total, or one line per organization for the site. */
function ManagedUsage({ usage, site }: { usage: RunnersView["usage"]; site: boolean }) {
  const minutes = (seconds: number) => (seconds / 60).toLocaleString(undefined, { maximumFractionDigits: 1 });
  const rows = usage?.rows ?? [];
  return (
    <Card>
      <CardHeader>
        <CpuIcon /> Managed runner usage in {usage?.month}
      </CardHeader>
      <CardContent className="text-xs text-muted-foreground">
        {rows.length === 0 ? (
          "No CI jobs have run on the managed runners this month."
        ) : site ? (
          <ul className="flex flex-col gap-1">
            {rows.map((r) => (
              <li key={r.owner}>
                <b className="font-mono text-foreground">{r.owner || "(no organization)"}</b>: {r.jobs} job{r.jobs === 1 ? "" : "s"}, {minutes(r.seconds)} min
                {r.failed > 0 && `, ${r.failed} failed`}
              </li>
            ))}
          </ul>
        ) : (
          <>
            <b className="text-foreground">{rows[0].jobs}</b> CI job{rows[0].jobs === 1 ? "" : "s"},{" "}
            <b className="text-foreground">{minutes(rows[0].seconds)}</b> runner minutes. This is what your organization is billed for; runners of your own are
            free.
          </>
        )}
        {!site && usage?.limit_minutes > 0 && (
          <p className={cn("mt-2", usage.capped && "font-semibold text-danger")}>
            {usage.capped
              ? `Monthly limit of ${usage.limit_minutes.toLocaleString()} runner minutes reached: new CI jobs wait in the queue until next month or a site admin raises it. Runners of your own are not limited.`
              : `Monthly limit: ${usage.limit_minutes.toLocaleString()} runner minutes.`}
          </p>
        )}
        {site && usage?.limits && (
          <p className="mt-2">
            Monthly limit per organization: {usage.limits.default_minutes > 0 ? `${usage.limits.default_minutes.toLocaleString()} min` : "none"}
            {Object.keys(usage.limits.orgs).length > 0 &&
              ` (${Object.keys(usage.limits.orgs).length} override${Object.keys(usage.limits.orgs).length === 1 ? "" : "s"})`}
            .
          </p>
        )}
      </CardContent>
    </Card>
  );
}

/** The switch that limits CI jobs to runners on a platform with saved deploy settings. */
function Enforce({ v, liveOn }: { v: RunnersView; liveOn: (p: RunnerPlatform) => number }) {
  const org = useScope();
  const act = useAction();
  const [confirm, setConfirm] = useState(false);
  const names = v.placements.map((s) => platformLabel(s.placement.platform)).join(" or ");
  const onPlaced = v.placements.reduce((n, s) => n + liveOn(s.placement.platform), 0);
  const heldBack = v.runners.filter(isLive).length - onPlaced;
  const set = (strict: boolean) => act.mutate({ method: "PATCH", path: `${adminPath(org, "/runners")}/placements`, body: { strict } });
  return (
    <CardContent className="border-t">
      <CheckboxField
        checked={v.strict}
        disabled={act.isPending}
        onCheckedChange={(c) => (c === true ? setConfirm(true) : set(false))}
        title={`Only runners on ${names} take CI jobs`}
        description={
          <>
            Runners elsewhere, or started without <code>--platform</code>, keep polling but get no CI work. Leave off while you move runners over.
          </>
        }
      />
      {act.error ? <ErrorMessage error={act.error} /> : null}
      <ConfirmDialog
        open={confirm}
        onOpenChange={setConfirm}
        title={`Give CI jobs only to runners on ${names}?`}
        description={
          onPlaced === 0
            ? `No online runner reports ${names} yet, so CI jobs wait in the queue until one starts.`
            : heldBack > 0
              ? `${heldBack} online runner${heldBack === 1 ? "" : "s"} on another platform (or none) will stop getting CI jobs.`
              : "Every online runner is on one of those platforms, so nothing changes now."
        }
        confirmLabel="Limit CI jobs"
        onConfirm={() => set(true)}
      />
    </CardContent>
  );
}
